import api from './api';

export type ControlLoteSerie = 'N' | 'L' | 'S';

export interface LoteAsignacion {
  idLote?: number | null;
  nroLote?: string | null;
  fechaVencimiento?: string | null;
  fechaFabricacion?: string | null;
  cantidad: number;
  observaciones?: string | null;
}

export interface LoteDeposito {
  nroUbicacion: number;
  nombreUbicacion?: string | null;
  cantidad: number;
}

export interface ProductoLote {
  idLote: number;
  codigoArticulo: string;
  descripcionArticulo?: string | null;
  nroLote: string;
  esSerie: boolean;
  fechaFabricacion?: string | null;
  fechaVencimiento?: string | null;
  estado: string;
  nroProveedor?: number | null;
  nroFormularioOrigen?: number | null;
  nroOrdenProduccionOrigen?: number | null;
  observaciones?: string | null;
  fechaAlta: string;
  usuarioAlta?: string | null;
  cantidadTotal: number;
  depositos: LoteDeposito[];
}

export interface LoteSugerencia {
  idLote: number;
  nroLote: string;
  fechaVencimiento?: string | null;
  cantidadDisponible: number;
  cantidadSugerida: number;
  esSerie: boolean;
  vencido: boolean;
}

export interface LoteNumeracion {
  idLoteNumeracion: number;
  nombre: string;
  mascara: string;
  ultimoNumero: number;
  ambito: string;
  codigoArticulo?: string | null;
  activo: boolean;
}

export interface LoteAdjunto {
  idAdjunto: number;
  idLote: number;
  tipoDocumento: string;
  nombreOriginal: string;
  contentType: string;
  tamanoBytes: number;
  descripcion?: string | null;
  usuarioAlta?: string | null;
  fechaAlta: string;
}

export interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

const loteService = {
  async search(params: {
    codigoArticulo?: string;
    nroLote?: string;
    estado?: string;
    nroUbicacion?: number;
    soloConSaldo?: boolean;
    page?: number;
    pageSize?: number;
  }): Promise<PagedResult<ProductoLote>> {
    const { data } = await api.get('/api/Lotes', { params });
    return data;
  },

  async getById(idLote: number): Promise<ProductoLote> {
    const { data } = await api.get(`/api/Lotes/${idLote}`);
    return data;
  },

  async create(payload: {
    codigoArticulo: string;
    nroLote: string;
    esSerie?: boolean;
    fechaFabricacion?: string | null;
    fechaVencimiento?: string | null;
    nroProveedor?: number | null;
    observaciones?: string | null;
  }): Promise<ProductoLote> {
    const { data } = await api.post('/api/Lotes', payload);
    return data;
  },

  async update(
    idLote: number,
    payload: {
      fechaFabricacion?: string | null;
      fechaVencimiento?: string | null;
      estado?: string;
      observaciones?: string | null;
    },
  ): Promise<ProductoLote> {
    const { data } = await api.put(`/api/Lotes/${idLote}`, payload);
    return data;
  },

  async sugerir(codigoArticulo: string, nroUbicacion: number, cantidad: number): Promise<LoteSugerencia[]> {
    const { data } = await api.get('/api/Lotes/sugerir', {
      params: { codigoArticulo, nroUbicacion, cantidad },
    });
    return data;
  },

  async listNumeracion(params?: { incluirInactivas?: boolean }): Promise<LoteNumeracion[]> {
    const { data } = await api.get('/api/Lotes/numeracion', {
      params: params?.incluirInactivas ? { incluirInactivas: true } : undefined,
    });
    return data;
  },

  async createNumeracion(payload: {
    nombre: string;
    mascara: string;
    ambito: string;
    codigoArticulo?: string | null;
  }): Promise<LoteNumeracion> {
    const { data } = await api.post('/api/Lotes/numeracion', payload);
    return data;
  },

  async updateNumeracion(
    id: number,
    payload: {
      nombre: string;
      mascara: string;
      ambito: string;
      codigoArticulo?: string | null;
      activo: boolean;
    },
  ): Promise<LoteNumeracion> {
    const { data } = await api.put(`/api/Lotes/numeracion/${id}`, payload);
    return data;
  },

  async deleteNumeracion(id: number): Promise<void> {
    await api.delete(`/api/Lotes/numeracion/${id}`);
  },

  async previewNumeracion(payload: {
    idLoteNumeracion?: number;
    mascara?: string;
    codigoArticulo?: string;
    cantidad?: number;
    fechaReferencia?: string;
  }): Promise<string[]> {
    const { data } = await api.post('/api/Lotes/numeracion/preview', payload);
    return data;
  },

  async generarNumeracion(payload: {
    idLoteNumeracion?: number;
    codigoArticulo?: string;
    esSerie?: boolean;
    cantidad?: number;
    fechaReferencia?: string;
  }): Promise<{ numeros: string[]; idLoteNumeracion: number; mascara: string }> {
    const { data } = await api.post('/api/Lotes/numeracion/generar', payload);
    return data;
  },

  async createConAdjunto(payload: {
    codigoArticulo: string;
    nroLote: string;
    esSerie?: boolean;
    fechaFabricacion?: string | null;
    fechaVencimiento?: string | null;
    nroProveedor?: number | null;
    observaciones?: string | null;
    archivo?: File | null;
    tipoDocumento?: string;
    descripcionAdjunto?: string;
  }): Promise<ProductoLote> {
    const form = new FormData();
    form.append('CodigoArticulo', payload.codigoArticulo);
    form.append('NroLote', payload.nroLote);
    if (payload.esSerie != null) form.append('EsSerie', String(payload.esSerie));
    if (payload.fechaFabricacion) form.append('FechaFabricacion', payload.fechaFabricacion);
    if (payload.fechaVencimiento) form.append('FechaVencimiento', payload.fechaVencimiento);
    if (payload.nroProveedor != null) form.append('NroProveedor', String(payload.nroProveedor));
    if (payload.observaciones) form.append('Observaciones', payload.observaciones);
    if (payload.archivo) form.append('archivo', payload.archivo);
    if (payload.tipoDocumento) form.append('tipoDocumento', payload.tipoDocumento);
    if (payload.descripcionAdjunto) form.append('descripcionAdjunto', payload.descripcionAdjunto);
    const { data } = await api.post('/api/Lotes/con-adjunto', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async listAdjuntos(idLote: number): Promise<LoteAdjunto[]> {
    const { data } = await api.get(`/api/Lotes/${idLote}/adjuntos`);
    return data;
  },

  async uploadAdjunto(
    idLote: number,
    file: File,
    tipoDocumento?: string,
    descripcion?: string,
  ): Promise<LoteAdjunto> {
    const form = new FormData();
    form.append('archivo', file);
    if (tipoDocumento) form.append('tipoDocumento', tipoDocumento);
    if (descripcion) form.append('descripcion', descripcion);
    const { data } = await api.post(`/api/Lotes/${idLote}/adjuntos`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async downloadAdjunto(idLote: number, idAdjunto: number, inline = false): Promise<Blob> {
    const { data } = await api.get(`/api/Lotes/${idLote}/adjuntos/${idAdjunto}`, {
      params: { inline },
      responseType: 'blob',
    });
    return data;
  },

  async deleteAdjunto(idLote: number, idAdjunto: number): Promise<void> {
    await api.delete(`/api/Lotes/${idLote}/adjuntos/${idAdjunto}`);
  },
};

export default loteService;
